import { ActionBar } from '@/components/layout/ActionBar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import {
  useBulkUpdateSharedKnowledgeFilesService,
  useSharedKnowledgeGroupsService,
} from '@/services/sharedFiles';
import { ChevronDown, Tag, Trash, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { DeleteManyFilesModal } from '../DeleteManyFilesModal';

interface FileTableActionBarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedRows: { id: string }[];
  onClearSelection: () => void;
}

export function FileTableActionBar({
  open,
  setOpen,
  selectedRows,
  onClearSelection,
}: FileTableActionBarProps) {
  const bulkUpdateMutation = useBulkUpdateSharedKnowledgeFilesService();
  const { groups } = useSharedKnowledgeGroupsService();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleClose = () => {
    onClearSelection();
    setOpen(false);
  };

  const handleGroupAssign = async (groupId: string) => {
    try {
      const fileIds = selectedRows.map(row => row.id);
      await bulkUpdateMutation.bulkUpdateFiles(fileIds, groupId);
      toast.success('Groups assigned successfully');
      onClearSelection();
      setOpen(false);
    } catch (error) {
      toast.error('Failed to assign groups');
    }
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleDeleteSuccess = () => {
    toast.success('Files deleted successfully');
    onClearSelection();
    setOpen(false);
  };

  return (
    <ActionBar open={open}>
      <FlexRow gap="6" alignItems="center" justifyContent="between" w="full">
        <FlexRow gap="2" alignItems="center">
          <Button variant="link" size="icon" onClick={handleClose}>
            <X className="text-white" />
          </Button>
          <Text variant="body-sm" className="text-white">
            {`${selectedRows.length} file(s) selected`}
          </Text>
        </FlexRow>
        <FlexRow gap="2">
          <Button variant="secondary" onClick={handleDeleteClick}>
            <Trash />
            Delete
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="secondary"
                disabled={bulkUpdateMutation.isPending}
              >
                <Tag />
                Assign Group
                <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {groups?.map(group => (
                <DropdownMenuItem
                  key={group.id}
                  onClick={() => handleGroupAssign(group.id)}
                >
                  {group.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </FlexRow>
      </FlexRow>
      <DeleteManyFilesModal
        fileIds={selectedRows.map(row => row?.id)}
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onSuccess={handleDeleteSuccess}
      />
    </ActionBar>
  );
}
